// utils/laneCopy.ts
//
// Some feature screens ship their own copy for English plus one regional
// language (`{ en: {...}, id: {...} }`). `useLaneCopy` makes them follow the
// user's preferred language: the screen's own language when it matches,
// otherwise the English copy with every sentence replaced by its translation
// from the locale files ("laneCopy.<scope>.<path>"). Non-text values (icons,
// functions, numbers) are kept as they are.
import { useMemo } from "react";
import i18n from "../i18n";
import { useAppLanguage } from "./appLanguage";

const localizeNode = (node: any, scope: string, path: string, lng: string): any => {
  if (typeof node === "string") {
    const key = `laneCopy.${scope}.${path}`;
    return i18n.exists(key, { lng }) ? i18n.t(key, { lng }) : node;
  }
  if (Array.isArray(node)) {
    return node.map((v, i) => localizeNode(v, scope, path ? `${path}.${i}` : String(i), lng));
  }
  if (node && typeof node === "object") {
    const out: Record<string, any> = {};
    for (const k of Object.keys(node)) {
      out[k] = localizeNode(node[k], scope, path ? `${path}.${k}` : k, lng);
    }
    return out;
  }
  return node;
};

/** The screen's copy in the user's preferred language. */
export function localizeCopy<T extends Record<string, any>>(
  scope: string,
  copy: Record<string, T>,
  lng: string,
  laneLang?: string,
): T {
  // The screen's own (regional) language wins, e.g. "ph" for Filipino.
  if (laneLang && laneLang !== "en" && copy[laneLang]) return copy[laneLang];
  if (copy[lng]) return copy[lng];
  return localizeNode(copy.en, scope, "", lng) as T;
}

export function useLaneCopy<T extends Record<string, any>>(
  scope: string,
  copy: Record<string, T>,
  laneLang?: string,
): T {
  const lng = useAppLanguage();
  return useMemo(
    () => localizeCopy(scope, copy, lng, laneLang),
    [scope, copy, lng, laneLang],
  );
}
